import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Container,
  SxProps,
  Theme
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  sx?: SxProps<Theme>;
}

const Navbar: React.FC<NavbarProps> = ({ sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <AppBar 
      position="static" 
      color="transparent" 
      elevation={0}
      sx={{ 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        ...sx
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Typography 
            variant="h6" 
            component={Link} 
            to="/"
            sx={{ 
              fontWeight: 700,
              textDecoration: 'none',
              color: 'white',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px',
              fontSize: { xs: '1.5rem', sm: '1.75rem' }
            }}
          >
            Plex
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                color="inherit" 
                component={Link} 
                to="/"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: isActive('/') ? 600 : 400,
                  borderBottom: isActive('/') ? '2px solid #667eea' : 'none',
                  borderRadius: 0,
                  px: 2,
                }}
              >
                Home
              </Button>
              <Button 
                color="inherit" 
                component={Link} 
                to="/prompts"
                sx={{ 
                  textTransform: 'none',
                  fontWeight: isActive('/prompts') ? 600 : 400,
                  borderBottom: isActive('/prompts') ? '2px solid #667eea' : 'none',
                  borderRadius: 0,
                  px: 2,
                }}
              >
                Prompts
              </Button>
              <Button 
                    color="inherit" 
                    component={Link} 
                    to="/setup"
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: isActive('/setup') ? 600 : 400,
                      borderBottom: isActive('/setup') ? '2px solid #667eea' : 'none',
                      borderRadius: 0,
                      px: 2,
                    }}
                  >
                    Setup
                  </Button>
            </Box>
          ) : (
            <>
              <IconButton 
                edge="end" 
                color="inherit" 
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: 'rgba(20, 20, 20, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    mt: 1,
                  }
                }}
              >
                <MenuItem 
                  onClick={() => handleNavigation('/')}
                  selected={isActive('/')}
                  sx={{
                    minWidth: '150px',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    }
                  }}
                >
                  Home
                </MenuItem>
                <MenuItem 
                  onClick={() => handleNavigation('/prompts')}
                  selected={isActive('/prompts')}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    }
                  }}
                >
                  Prompts
                </MenuItem>
                <MenuItem 
    onClick={() => handleNavigation('/setup')}
    selected={isActive('/setup')}
    sx={{
      '&.Mui-selected': {
        backgroundColor: 'rgba(102, 126, 234, 0.15)',
      },
      '&:hover': {
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
      }
    }}
  >
    Setup
  </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;